/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';
import { useParams } from 'js/lib/useRouter';

import { Button, Dialog, TextField } from '@coursera/cds-core';
import { LinkIcon } from '@coursera/cds-icons';

import { useEnterpriseBadge } from 'bundles/enterprise-admin-learning-path/hooks';
import { TrackedButton } from 'bundles/enterprise-ui/components';
import { copyText } from 'bundles/enterprise-ui/utils';

import _t from 'i18n!nls/enterprise-admin-program-catalog';

export const ShareCollectionDialog = ({
  badgeTemplateId,
  collectionName,
  collectionId,
  programId,
  open,
  onClose,
}: {
  collectionName: string;
  collectionId: string;
  programId: string;
  open: boolean;
  badgeTemplateId?: string | null;
  onClose: () => void;
}) => {
  const { programSlug } = useParams();
  const enterpriseBadge = useEnterpriseBadge(programId, badgeTemplateId ?? undefined);
  const link = badgeTemplateId
    ? `${config.url.base}programs/${programSlug}/learning-path/${enterpriseBadge.badge?.slug}?collectionId=${collectionId}&source=share`
    : `${config.url.base}programs/${programSlug}/collections/${programId}~${collectionId}?source=share`;
  const [status, setStatus] = React.useState<'success' | 'error' | undefined>();
  const [label, setLabel] = React.useState<string | undefined>();
  return (
    <Dialog open={open} onClose={onClose}>
      <Dialog.HeadingGroup>
        {collectionName
          ? _t('Share “#{collectionName}” with your learners', { collectionName })
          : _t('Share with your learners')}
      </Dialog.HeadingGroup>
      <Dialog.Content>
        <TextField
          aria-label={_t('Collection link')}
          value={link}
          fullWidth
          readOnly
          validationStatus={status}
          validationLabel={label}
        />
      </Dialog.Content>
      <Dialog.Actions
        css={css`
          display: flex;
          gap: var(--cds-spacing-200);
        `}
      >
        <TrackedButton
          trackingName="copy_collection_link"
          trackingData={{ enterpriseProgramId: programId, collectionId }}
          variant="primary"
          icon={<LinkIcon />}
          iconPosition="before"
          onClick={async () => {
            try {
              copyText(link);
              setStatus('success');
              setLabel(_t('Link copied to clipboard'));
            } catch {
              setStatus('error');
              setLabel(_t('Link failed to copy to clipboard'));
            }
          }}
        >
          {_t('Copy link')}
        </TrackedButton>
        <Button variant="secondary" onClick={onClose}>
          {_t('Cancel')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
